import React, { useMemo } from 'react';
import { Clock, FileText, AlertCircle } from 'lucide-react';
import clsx from 'clsx';
import Badge from '@components/common/Badge';
import { Assessment, AssessmentItem } from '@app-types';
import styles from './EvidenceRequirementsSummary.module.css';

interface EvidenceRequirementsSummaryProps {
  assessmentItems: AssessmentItem[];
  assessment: Assessment;
}

const EvidenceRequirementsSummary: React.FC<EvidenceRequirementsSummaryProps> = ({ assessmentItems, assessment }) => {
  // Filter processes that require evidence
  const evidenceRequired = useMemo(() => {
    return assessmentItems.filter((item) => item.evaluation === 'Implemented' && item.requires_evidence);
  }, [assessmentItems]);

  // Calculate evidence due date (30 days after meeting date if not provided by backend)
  const evidenceDueDate = useMemo(() => {
    if (assessment.evidence_due_date) {
      return new Date(assessment.evidence_due_date);
    }
    // Fallback: Calculate as 30 days after scheduled_date or completed_date
    const baseDate = assessment.completed_date || assessment.scheduled_date;
    if (baseDate) {
      const date = new Date(baseDate);
      date.setDate(date.getDate() + 30);
      return date;
    }
    return null;
  }, [assessment.evidence_due_date, assessment.completed_date, assessment.scheduled_date]);

  // Calculate days remaining/overdue
  const dueDateInfo = useMemo(() => {
    if (!evidenceDueDate) return null;

    const now = new Date();
    const diffTime = evidenceDueDate.getTime() - now.getTime();
    const daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    let urgency: 'critical' | 'warning' | 'info';
    let message: string;

    if (daysRemaining < 0) {
      urgency = 'critical';
      message = `${Math.abs(daysRemaining)} day${Math.abs(daysRemaining) !== 1 ? 's' : ''} overdue`;
    } else if (daysRemaining < 3) {
      urgency = 'critical';
      message = `${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} remaining`;
    } else if (daysRemaining <= 7) {
      urgency = 'warning';
      message = `${daysRemaining} days remaining`;
    } else {
      urgency = 'info';
      message = `${daysRemaining} days remaining`;
    }

    return { urgency, message, daysRemaining };
  }, [evidenceDueDate]);

  // Format date if available
  const formatDate = (date: Date | null) => {
    if (!date) return null;
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formattedDueDate = formatDate(evidenceDueDate);

  // Don't show if no evidence required (check after all hooks)
  if (evidenceRequired.length === 0) {
    return null;
  }

  return (
    <div className={styles.evidenceRequirementsCard}>
      <div className={styles.evidenceRequirementsHeader}>
        <FileText size={24} />
        <h3>Evidence Required</h3>
      </div>

      <div className={styles.evidenceRequirementsSummary}>
        <AlertCircle size={20} className={styles.summaryIcon} />
        <p className={styles.summaryText}>
          <strong>{evidenceRequired.length}</strong> process{evidenceRequired.length !== 1 ? 'es' : ''} require evidence
          submission from the assigned Contract Owner or Contract Manager
        </p>
      </div>

      {formattedDueDate && dueDateInfo && (
        <div
          className={clsx(
            styles.evidenceDueDate,
            styles[`evidenceDueDate${dueDateInfo.urgency.charAt(0).toUpperCase() + dueDateInfo.urgency.slice(1)}`]
          )}
        >
          <div className={styles.dueDateMain}>
            <Clock size={18} />
            <div className={styles.dueDateDetails}>
              <span className={styles.dueDateLabel}>Evidence Due:</span>
              <span className={styles.dueDateValue}>{formattedDueDate}</span>
            </div>
          </div>
          <div
            className={clsx(
              styles.dueDateCountdown,
              styles[`dueDateCountdown${dueDateInfo.urgency.charAt(0).toUpperCase() + dueDateInfo.urgency.slice(1)}`]
            )}
          >
            {dueDateInfo.message}
          </div>
        </div>
      )}

      <div className={styles.evidenceProcessList}>
        <h4>Processes Requiring Evidence:</h4>
        <ul>
          {evidenceRequired.map((item) => (
            <li key={item.id} className={styles.evidenceProcessItem}>
              <div className={styles.processInfo}>
                <span className={styles.processName}>{item.process_name}</span>
                <span className={styles.processDomain}>{item.domain_name}</span>
              </div>
              <Badge variant="warning">Evidence Required</Badge>
            </li>
          ))}
        </ul>
      </div>

      <div className={styles.evidenceRequirementsNote}>
        <p>
          <strong>Note:</strong> Assigned Contract Owner(s) and Contract Manager(s) have been notified and will upload evidence for these processes.
          You will be notified when evidence is submitted for review.
        </p>
      </div>
    </div>
  );
};

export default EvidenceRequirementsSummary;
